import React, { useState } from 'react';
import { toast } from 'sonner';
import { LoaderCircleIcon, LockIcon, RefreshCwIcon, XIcon } from 'lucide-react';
import { CommissionLine } from '../../types/commission';
import { formatUsd } from '../../utils/format';
import { Button } from '../sf/Button';
import { Money } from '../sf/Money';
import { NewBadge } from '../sf/NewBadge';
import { StatusBadge } from '../sf/StatusBadge';
import { StoredReference } from '../sf/StoredReference';
import { Tooltip } from '../sf/Tooltip';
import { AdjustedAmountBlock } from './AdjustedAmountBlock';
interface WhyAmountPanelProps {
  line: CommissionLine;
  onClose: () => void;
}
export function WhyAmountPanel({
  line,
  onClose
}: WhyAmountPanelProps) {
  const [isRecalculating, setIsRecalculating] = useState(false);
  const isLocked = line.periodStatus === 'Locked';
  const signed = line.status === 'Adjustment' || line.status === 'Manual';
  function handleRecalculate() {
    setIsRecalculating(true);
    window.setTimeout(() => {
      setIsRecalculating(false);
      toast.success('Recalculated — no change', {
        description: `${line.component} for ${line.payee}: Adjusted ${formatUsd(line.adjustedAmount)} (pays).`
      });
    }, 900);
  }
  return <aside aria-label="Why this amount" className="rounded border border-sf-border bg-white shadow-card" data-id="element-540" data-mp="2z1zub3sgze9o">
      <header className="flex items-start justify-between gap-2 border-b border-sf-border px-4 py-3" data-id="element-541" data-mp="2u48186g39m3w">
        <div className="min-w-0" data-id="element-542" data-mp="26e3ys2emzseh">
          <h2 className="flex flex-wrap items-center gap-2 text-sm font-bold" data-id="element-543" data-mp="22cipss583y9k">
            Why this amount? <NewBadge data-id="element-544" data-mp="2g3drnkhb1f3n" />
          </h2>
          <p className="truncate text-xs text-sf-weak" data-id="element-545" data-mp="2g4ffkpjf5w2s">
            {line.payee} · {line.component} · {line.loanNumber}
          </p>
          <p className="mt-0.5 text-[11px] text-sf-weak" data-id="element-546" data-mp="2baug0t26gu1h">Line-level traces are proposed. Today there is one fee row per payee per loan.</p>
        </div>
        <button type="button" aria-label="Close explainer" onClick={onClose} className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-sf-weak transition-colors duration-150 ease-out hover:bg-sf-bg hover:text-sf-text" data-id="element-547" data-mp="1ip1h4uk05zjz">
          <XIcon className="h-4 w-4" data-id="element-548" data-mp="3sfsrwzf7w96k" />
        </button>
      </header>

      <div className="space-y-5 px-4 py-4" data-id="element-549" data-mp="3siopunr2exv0">
        <div data-id="element-550" data-mp="18zqbugii81i">
          <p className="text-[11px] text-sf-weak" data-id="element-551" data-mp="18q1wbb488xx">Adjusted · Adjusted Loan Amount × BPS — pays</p>
          <p className="mt-0.5 text-[26px] font-bold leading-none" data-id="element-552" data-mp="2zsyicxjxuu7v">
            <Money value={line.adjustedAmount} signed={signed} data-id="element-553" data-mp="34q870azi71un" />
          </p>
          <p className="mt-1.5" data-id="element-554" data-mp="39pulenr2io51">
            <StoredReference value={line.payAmount} signed={signed} data-id="element-555" data-mp="39ortqdlx8ho2" />
          </p>
          <p className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-sf-weak" data-id="element-556" data-mp="3drxyfgtdy0sf">
            <StatusBadge status={line.status} data-id="element-557" data-mp="ozvod9bk9zh7" />
            {line.bpsSource && `${line.bpsSource} · Bonus: none`}
          </p>
        </div>

        <section aria-label="How it was calculated" data-id="element-558" data-mp="szw7365p0wu2">
          <h3 className="mb-2 text-xs font-semibold text-sf-weak" data-id="element-559" data-mp="2dizy631vvifh">How it was calculated</h3>
          <ol data-id="element-560" data-mp="29hmnnfnuir28">
            {line.trace.map((step, index) => <li key={step} className="flex gap-3" data-id="element-561" data-mp="29ifzuw5kq8rr" data-mpi={index}>
                <div className="flex flex-col items-center" data-id="element-562" data-mp="24qedgheoz1o0">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-sf-border-strong bg-white text-[11px] font-semibold text-sf-weak" data-id="element-563" data-mp="24ol7l4bwklh2">
                    {index + 1}
                  </span>
                  {index < line.trace.length - 1 && <span className="my-1 w-px flex-1 bg-sf-border-strong" aria-hidden="true" data-id="element-564" data-mp="1ps3xegfg9cw3" />}
                </div>
                <p className={`pb-3 pt-0.5 text-[13px] leading-snug ${step.startsWith('Self-gen') ? 'font-semibold text-sf-amber-text' : ''}`} data-id="element-565" data-mp="1lsbuaj8tryy4">{step}</p>
              </li>)}
          </ol>
          <div className="ml-8 space-y-1 rounded border border-sf-border bg-sf-bg px-3 py-2" data-id="element-566" data-mp="3g5bg4u3stpdu">
            {line.formulas.map((formula, index) => <p key={formula} className={`font-mono text-xs ${index === 0 ? 'font-semibold text-sf-text' : 'text-sf-weak'}`} data-id="element-567" data-mp="27vkp0vv87ker" data-mpi={index}>
                {formula}
              </p>)}
          </div>
        </section>

        {line.adjustedCalc && <AdjustedAmountBlock calc={line.adjustedCalc} data-id="element-568" data-mp="1yv4qm0tcb3gr" />}

        <section aria-label="Input snapshot" data-id="element-569" data-mp="23tzg1lltbq9u">
          <h3 className="mb-2 text-xs font-semibold text-sf-weak" data-id="element-570" data-mp="23so6f55oroat">
            Input snapshot <span className="font-normal" data-id="element-571" data-mp="1p41qb188isij">· frozen at calculation</span>
          </h3>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded border border-sf-border px-3 py-2.5" data-id="element-572" data-mp="1txcwr194x79x">
            {line.snapshot.map(field => <div key={field.label} className="min-w-0" data-id="element-573" data-mp="1k6i0d426crar">
                <dt className="text-[11px] text-sf-weak" data-id="element-574" data-mp="1k71jgo3k9lqd">{field.label}</dt>
                <dd className="text-[13px] tabular-nums" data-id="element-575" data-mp="166h0nrxnbpwv">{field.value}</dd>
              </div>)}
          </dl>
        </section>

        <footer className="flex items-center justify-between gap-3 border-t border-sf-border pt-3" data-id="element-576" data-mp="1b51zbmx921j7">
          <NewBadge label="NEW · on-demand recalculation" data-id="element-577" data-mp="rka4t7sdyt8f" />
          {isLocked ? <Tooltip content={`${line.period} is locked. Changes post as Adjustment lines to the next open period.`} align="end" data-id="element-578" data-mp="mmi53rli22to">
              <Button disabled icon={<LockIcon data-id="element-580" data-mp="wilhrrdz9juv" />} data-id="element-579" data-mp="11fv613pmrph4">
                Recalculate
              </Button>
            </Tooltip> : <Button onClick={handleRecalculate} disabled={isRecalculating} icon={isRecalculating ? <LoaderCircleIcon className="animate-spin" data-id="element-582" data-mp="15kcmou0p1mno" /> : <RefreshCwIcon data-id="element-583" data-mp="15jjctk6fuh1b" />} data-id="element-581" data-mp="1ai4g5sgzlgtz">
              {isRecalculating ? 'Recalculating…' : 'Recalculate'}
            </Button>}
        </footer>
      </div>
    </aside>;
}